<template>
  <v-card outlined class="pa-4 mb-4 planning-scope">
    <div class="text-subtitle-2 font-weight-bold">Für welche Monate sollen diese Einstellungen gelten?</div>
    <v-radio-group :value="value" :disabled="disabled" hide-details class="mt-2" @change="changeScope">
      <v-radio :label="month ? 'Nur für ' + monthLabel(month) : 'Nur für diesen Monat'" value="month" color="green"></v-radio>
      <v-radio label="Für diesen und künftige Monate" value="future" color="green"></v-radio>
    </v-radio-group>
    <template v-if="value === 'future'">
      <p class="text-body-2 mt-3 mb-2">
        Fixe Schichten, Sperrtage und andere regelmässige Einstellungen werden auch ab
        <strong>{{ monthLabel(impact.effectiveFrom) }}</strong> verwendet.
      </p>
      <p v-if="impact.until" class="text-body-2 mb-2">
        Ab <strong>{{ monthLabel(impact.until) }}</strong> sind bereits andere Einstellungen gespeichert. Diese gelten dann weiter.
      </p>
      <p class="text-body-2 mb-3">
        Pensum, Stundenkorrekturen und die Verrechnung von Überstunden werden nur für <strong>{{ monthLabel(month) }}</strong> gespeichert.
        Das gilt auch für die Freigabe zur Planung und einzelne ausgefallene Schultage.
      </p>
      <div v-if="impact.months.length" class="planning-scope-months">
        <div class="text-body-2 font-weight-bold mb-2">
          {{ impact.months.length === 1 ? 'Ein Monat hat bereits eigene Einstellungen' : impact.months.length + ' Monate haben bereits eigene Einstellungen' }}
        </div>
        <p class="text-body-2 mb-3">
          Die hier aufgeführten Einstellungen werden nicht automatisch geändert.
          Wähle die Monate aus, für die du die neuen Einstellungen ebenfalls verwenden möchtest.
        </p>
        <div v-for="entry in impact.months" :key="entry.month" class="text-body-2 mb-3">
          <strong>{{ monthLabel(entry.month) }}</strong>
          <div>{{ fieldLabels(entry) }}</div>
          <div v-if="selectedMonths.includes(entry.month)" class="font-weight-bold">Wird beim Speichern angepasst.</div>
        </div>
        <v-btn outlined class="text-none" :disabled="disabled" @click="openSelection">Monate auswählen…</v-btn>
      </div>
      <p v-else class="text-body-2 mb-0">Du musst keine weiteren Monate auswählen.</p>
    </template>
    <v-dialog v-model="selectionOpen" max-width="600" :persistent="disabled">
      <v-card>
        <v-card-title>Einstellungen auch für diese Monate übernehmen</v-card-title>
        <v-card-text class="text-body-2">
          <p>Wähle die Monate aus, die ebenfalls die neuen Einstellungen verwenden sollen. Die bisherigen regelmässigen Einstellungen dieser Monate werden ersetzt.</p>
          <div class="planning-scope-selection">
            <v-checkbox v-for="entry in impact.months" :key="entry.month" v-model="pendingMonths"
              :value="entry.month" :label="monthLabel(entry.month)" :hint="fieldLabels(entry)" persistent-hint
              :disabled="disabled" color="green" class="mt-2"></v-checkbox>
          </div>
          <p class="text-body-2 mt-4 mb-0">
            Das Pensum und andere Angaben zum einzelnen Monat bleiben erhalten.
            Bereits eingeteilte Schichten werden dadurch nicht verschoben oder neu geplant.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="text-none" :disabled="disabled" @click="selectionOpen = false">Abbrechen</v-btn>
          <v-btn color="primary" class="text-none" :disabled="disabled || (!pendingMonths.length && !selectedMonths.length)" @click="confirmSelection">
            {{ !pendingMonths.length ? 'Auswahl aufheben' : autoSave ? 'Übernehmen und speichern' : 'Weiter zur Vorschau' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { FIELD_LABELS, standardImpact, standardUpdateRequest } from "./planningStandards"

export default {
  name: "PlanningSettingsScope",
  props: {
    value: { type: String, default: "month" },
    month: { type: String, required: true },
    studioSettings: { type: Object, default: () => ({}) },
    settings: { type: Object, required: true },
    selectedMonths: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    autoSave: { type: Boolean, default: false },
  },
  data: () => ({ selectionOpen: false, pendingMonths: [] }),
  computed: {
    impact() { return standardImpact(this.studioSettings, this.month, this.settings) },
  },
  methods: {
    monthLabel(month) {
      const [year, number] = month.split("-").map(Number)
      return new Intl.DateTimeFormat("de-CH", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(year, number - 1, 1)))
    },
    fieldLabels(entry) {
      return entry.changedFields.length ? "Andere Einstellungen bei: " + entry.changedFields.map((field) => FIELD_LABELS[field]).join(", ") : "Gleiche Einstellungen, aber separat für diesen Monat gespeichert"
    },
    changeScope(value) {
      this.selectionOpen = false
      this.$emit("input", value)
    },
    openSelection() {
      this.pendingMonths = [...this.selectedMonths]
      this.selectionOpen = true
    },
    confirmSelection() {
      const request = standardUpdateRequest(this.studioSettings, this.month, this.settings, this.pendingMonths)
      this.selectionOpen = false
      this.$emit("adopt", request)
    },
  },
}
</script>

<style scoped>
.planning-scope { border-radius: 12px; }
.planning-scope-months, .planning-scope-selection { max-height: 280px; overflow-y: auto; }
</style>
